import {
    fetchItemShippingEstimateIfNeeded,
    getShippingEstimate,
    isShippingEstimateLoading,
} from '@/redux/modules/shipping';
import React, { useState } from 'react';
import { ShippingLineIcon, ShippingLineLabel } from './helpers/styles';
import { useDispatch, useSelector } from 'react-redux';
import ActionText from '@liveauctioneers/caterwaul-components/lib/ActionText/ActionText';
import CountriesWithISOCodes from '@liveauctioneers/caterwaul-components/lib/Intl/CountriesWithISOCodes';
import { faShippingFast } from '@fortawesome/pro-duotone-svg-icons/faShippingFast';
import { faSpinner } from '@fortawesome/pro-duotone-svg-icons/faSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import { FormattedMessage } from 'react-intl';
import { getCatalog } from '@/redux/modules/catalog';
import { getCatalogDetail } from '@/redux/modules/catalogDetail';
import { getHasRequiredDimensions } from '@/redux/modules/itemDetail';
import { getItemShipping } from '@/redux/modules/itemShipping';
import { getSellerInArtaBeta } from '@/redux/modules/seller';
import InfoBadge from './InfoBadge';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';

type Props = {
    catalogId: number;
    country: string;
    itemId: number;
    onClick: (event: any) => void;
    sellerId: number;
    state: string;
    zip: string;
};

const ShippingCostLine = ({ catalogId, country, itemId, onClick, sellerId, state, zip }: Props) => {
    const { domesticFlatShipping } = useSelector((state) => getItemShipping(state, itemId));
    const { inHouseShipping } = useSelector((state) => getCatalogDetail(state, catalogId));
    const hasArtaShipping = useSelector((state) => getSellerInArtaBeta(state, sellerId));
    const hasRequiredDimensions = useSelector((state) => getHasRequiredDimensions(state, itemId));
    const { currency: domesticFlatShippingCurrency } = useSelector((state) => getCatalog(state, catalogId), isEqual);
    const shippingEstimate = useSelector((state) => getShippingEstimate(state, itemId));
    const hasError = shippingEstimate === 'error';
    const { currency: shippingCurrency, total: shippingCost } = shippingEstimate;
    const dispatch = useDispatch();
    const [canQuote, setCanQuote] = useState(country && zip && hasArtaShipping);
    const loadingQuote = useSelector((state) => isShippingEstimateLoading(state, itemId));

    if (country && zip && hasArtaShipping && !canQuote) {
        setCanQuote(true);
    }

    const fetchQuote = () => {
        const { code: countryCode } =
            CountriesWithISOCodes.find((currCountry) => currCountry.name === country || currCountry.code === country) ||
            {};
        // @ts-ignore
        dispatch(fetchItemShippingEstimateIfNeeded({ countryCode, itemId, postalCode: zip }));
    };

    // User is either not logged-in or doesn't have an address in profile
    const buyerLocationUnknown = !country || !zip;
    const buyerInUS = state && country && ['UNITED STATES', 'US'].includes(country.toUpperCase());
    const buyerInContiguousUS = buyerInUS && !['ALASKA', 'AK', 'HAWAII', 'HI'].includes(state.toUpperCase());
    const hasDomesticFlatRateShipping = domesticFlatShipping !== null && domesticFlatShipping > 0;
    const hasDomesticFreeShipping = domesticFlatShipping === 0;

    let shippingCostText = null;

    if (hasArtaShipping && hasRequiredDimensions) {
        if (loadingQuote) {
            shippingCostText = <StyledIcon icon={faSpinner} spin />;
        } else if (hasError) {
            shippingCostText = <FormattedMessage id="shippingInfo.shippingCost.unable" />;
        } else if (domesticFlatShipping === null) {
            if (buyerLocationUnknown || (!shippingCost && !zip)) {
                shippingCostText = <FormattedMessage id="shippingInfo.shippingCost.ratesVaryByDestination" />;
            } else if (!shippingCost) {
                shippingCostText = (
                    <StyledShippingRequestActionText onClick={fetchQuote}>
                        <FormattedMessage id="shippingInfo.shippingCost.getShippingQuote" />
                    </StyledShippingRequestActionText>
                );
            } else {
                shippingCostText = (
                    <>
                        <FormattedCurrency sourceCurrency={shippingCurrency} value={shippingCost} />{' '}
                        <FormattedMessage id="shippingInfo.shippingCost.shippingQuote" />
                    </>
                );
            }
        }
    } else {
        if (domesticFlatShipping === null) {
            if (!inHouseShipping) {
                /**
                 * FIXME: temporarily disable "buyer arranges shipping" line when house doesn't offer in-house shipping
                 * @see https://liveauctioneers.atlassian.net/browse/FPM-3353
                 */
                shippingCostText = <FormattedMessage id="shippingInfo.shippingCost.seePolicyForShipping" />;

                // TODO: restore this line
                // shippingCostText = <FormattedMessage id="shippingInfo.shippingCost.buyerArrangesShipping" />;
            } else {
                shippingCostText = <FormattedMessage id="shippingInfo.shippingCost.offersInHouseShipping" />;
            }
        }
    }

    // Applies to both ARTA and non-ARTA shipping
    if (hasDomesticFreeShipping) {
        if (buyerLocationUnknown) {
            shippingCostText = <FormattedMessage id="shippingInfo.shippingCost.freeShippingToContiguousUS" />;
        } else if (buyerInContiguousUS) {
            shippingCostText = <FormattedMessage id="shippingInfo.shippingCost.freeShipping" />;
        } else {
            shippingCostText = <FormattedMessage id="shippingInfo.shippingCost.seePolicyForShipping" />;
        }
    } else if (hasDomesticFlatRateShipping) {
        if (buyerLocationUnknown) {
            shippingCostText = (
                <>
                    <FormattedCurrency sourceCurrency={domesticFlatShippingCurrency} value={domesticFlatShipping} />{' '}
                    <FormattedMessage id="shippingInfo.shippingCost.flatRateShippingToContiguousUS" />
                </>
            );
        } else if (buyerInContiguousUS) {
            shippingCostText = (
                <>
                    <FormattedCurrency sourceCurrency={domesticFlatShippingCurrency} value={domesticFlatShipping} />{' '}
                    <FormattedMessage id="shippingInfo.shippingCost.flatRate" />
                </>
            );
        } else {
            shippingCostText = <FormattedMessage id="shippingInfo.shippingCost.seePolicyForShipping" />;
        }
    }

    if (!shippingCostText) {
        return null;
    }

    return (
        <>
            <ShippingLineIcon icon={faShippingFast} />
            <ShippingLineLabel>{shippingCostText}</ShippingLineLabel>
            <InfoBadge onClick={onClick} />
        </>
    );
};

export default React.memo<Props>(ShippingCostLine, isEqual);

const StyledIcon = styled(FontAwesomeIcon)`
    color: ${({ theme }) => theme.colors.grey100};
`;

const StyledShippingRequestActionText = styled(ActionText)`
    font-family: ${({ theme }) => theme.typography.fontPrimary};
    color: ${({ theme }) => theme.colors.blue100};
    letter-spacing: 0.5px;
    text-decoration: underline;

    &:hover {
        color: ${({ theme }) => theme.colors.blue200};
    }
`;
